<template>
    <div class="templates-management">
        <div class="tm-header">
            <h1 class="tm-title">{{ $t('dash.templates.management.title') }}</h1>
        </div>

        <div class="tm-tabs">
            <div
                v-for="t in TYPES"
                :key="t"
                class="tm-tab"
                :class="{ active: activeType === t }"
                @click="switchType(t)"
            >{{ $t(`dash.templates.type.${t}`) }}</div>
        </div>

        <div class="tm-body">
            <div class="tm-tree">
                <TemplatePicker
                    :key="activeType"
                    inline
                    :type="activeType"
                    mode="manage"
                    :selected-id="creating ? null : (selected && selected.id)"
                    @select="onSelect"
                    @deleted="onDeleted"
                />
            </div>

            <div class="tm-detail">
                <!-- no "post" (Publications) creation: it stores the full saveAllFormData a real
                     post creator session builds, nothing a from-scratch Settings form could
                     reasonably reconstruct - only ever created by saving one from the post
                     creator itself, same as before. -->
                <div class="tm-detail-header" v-if="activeType !== 'post'">
                    <div class="tm-btn-new" @click="startCreate">
                        <i class="fa-regular fa-plus"></i>
                        {{ $t('dash.templates.management.new-template') }}
                    </div>
                </div>

                <div v-if="creating" class="tm-create-form">
                    <label class="tm-field-label">{{ $t('dash.templates.name-label') }}</label>
                    <input class="tm-input" v-model="createName" type="text" :placeholder="$t('dash.templates.name-placeholder')">

                    <template v-if="activeType === 'text'">
                        <label class="tm-field-label">{{ $t('dash.templates.management.text-label') }}</label>
                        <div class="tm-emoji-field">
                            <textarea ref="createText" class="tm-textarea" v-model="createText" @input="sizeTextarea('createText')"></textarea>
                            <i class="fa-regular fa-face-laugh tm-emoji-trigger" @click.stop="toggleEmojiPicker('createText')"></i>
                            <Picker v-if="openEmojiPickerFor === 'createText'" :data="emojiIndex" class="tm-emoji-picker" :color="'#b64985'" @select="e => insertEmoji('createText', e)" />
                        </div>
                    </template>

                    <template v-else-if="activeType === 'affix'">
                        <label class="tm-field-label">{{ $t('dash.templates.management.prefix-label') }}</label>
                        <div class="tm-emoji-field">
                            <textarea ref="createPrefix" class="tm-input tm-affix-textarea" v-model="createPrefix" @input="sizeTextarea('createPrefix')"></textarea>
                            <i class="fa-regular fa-face-laugh tm-emoji-trigger" @click.stop="toggleEmojiPicker('createPrefix')"></i>
                            <Picker v-if="openEmojiPickerFor === 'createPrefix'" :data="emojiIndex" class="tm-emoji-picker" :color="'#b64985'" @select="e => insertEmoji('createPrefix', e)" />
                        </div>
                        <label class="tm-field-label">{{ $t('dash.templates.management.suffix-label') }}</label>
                        <div class="tm-emoji-field">
                            <textarea ref="createSuffix" class="tm-input tm-affix-textarea" v-model="createSuffix" @input="sizeTextarea('createSuffix')"></textarea>
                            <i class="fa-regular fa-face-laugh tm-emoji-trigger" @click.stop="toggleEmojiPicker('createSuffix')"></i>
                            <Picker v-if="openEmojiPickerFor === 'createSuffix'" :data="emojiIndex" class="tm-emoji-picker" :color="'#b64985'" @select="e => insertEmoji('createSuffix', e)" />
                        </div>
                    </template>

                    <template v-else-if="activeType === 'media'">
                        <label class="tm-field-label">{{ $t('dash.templates.management.media-label') }}</label>
                        <MediaTemplateUploader :key="createUploaderKey" v-model="createFiles" />
                    </template>

                    <div class="tm-form-actions">
                        <div class="tm-btn-cancel" @click="cancelCreate">{{ $t('dash.button.cancel') }}</div>
                        <button class="tm-btn-save" :disabled="!canCreate" @click="submitCreate">{{ $t('dash.button.add') }}</button>
                    </div>
                </div>

                <div v-else-if="!selected" class="tm-empty">
                    {{ $t('dash.templates.management.empty-selection') }}
                </div>

                <template v-else>
                    <h3 class="tm-detail-name">{{ selected.name }}</h3>
                    <p v-if="selected.source" class="tm-detail-source">{{ $t(`dash.templates.source.${selected.source}`) }}</p>

                    <template v-if="selected.type === 'text'">
                        <label class="tm-field-label">{{ $t('dash.templates.management.text-label') }}</label>
                        <div class="tm-emoji-field">
                            <textarea ref="editText" class="tm-textarea" v-model="editText" @input="sizeTextarea('editText')"></textarea>
                            <i class="fa-regular fa-face-laugh tm-emoji-trigger" @click.stop="toggleEmojiPicker('editText')"></i>
                            <Picker v-if="openEmojiPickerFor === 'editText'" :data="emojiIndex" class="tm-emoji-picker" :color="'#b64985'" @select="e => insertEmoji('editText', e)" />
                        </div>
                        <button class="tm-btn-save" :disabled="saving" @click="saveText">{{ $t('dash.button.save') }}</button>
                    </template>

                    <template v-else-if="selected.type === 'affix'">
                        <label class="tm-field-label">{{ $t('dash.templates.management.prefix-label') }}</label>
                        <div class="tm-emoji-field">
                            <textarea ref="editPrefix" class="tm-input tm-affix-textarea" v-model="editPrefix" @input="sizeTextarea('editPrefix')"></textarea>
                            <i class="fa-regular fa-face-laugh tm-emoji-trigger" @click.stop="toggleEmojiPicker('editPrefix')"></i>
                            <Picker v-if="openEmojiPickerFor === 'editPrefix'" :data="emojiIndex" class="tm-emoji-picker" :color="'#b64985'" @select="e => insertEmoji('editPrefix', e)" />
                        </div>
                        <label class="tm-field-label">{{ $t('dash.templates.management.suffix-label') }}</label>
                        <div class="tm-emoji-field">
                            <textarea ref="editSuffix" class="tm-input tm-affix-textarea" v-model="editSuffix" @input="sizeTextarea('editSuffix')"></textarea>
                            <i class="fa-regular fa-face-laugh tm-emoji-trigger" @click.stop="toggleEmojiPicker('editSuffix')"></i>
                            <Picker v-if="openEmojiPickerFor === 'editSuffix'" :data="emojiIndex" class="tm-emoji-picker" :color="'#b64985'" @select="e => insertEmoji('editSuffix', e)" />
                        </div>
                        <button class="tm-btn-save" :disabled="saving" @click="saveAffix">{{ $t('dash.button.save') }}</button>
                    </template>

                    <template v-else-if="selected.type === 'media' && !selectedHasOverlay">
                        <label class="tm-field-label">{{ $t('dash.templates.management.media-label') }}</label>
                        <MediaTemplateUploader :key="selected.id" v-model="editFiles" :initial-files="(selected.data && selected.data.files) || []" />
                        <button class="tm-btn-save" :disabled="saving || !editFiles.length" @click="saveMedia">{{ $t('dash.button.save') }}</button>
                    </template>

                    <template v-else>
                        <div class="tm-preview-large">
                            <TemplatePreview :template="selected" show-post-preview />
                        </div>
                        <p class="tm-readonly-hint">
                            {{ $t(selected.type === 'post' ? 'dash.templates.management.post-readonly' : 'dash.templates.management.media-overlay-readonly') }}
                        </p>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast'
import emojiData from '@/constants/emoji-mart-vue-fast/all.json'
import TemplatePicker from '@/components/dash/shared/TemplatePicker.vue'
import TemplatePreview from '@/components/dash/shared/TemplatePreview.vue'
import MediaTemplateUploader from './MediaTemplateUploader.vue'
import TemplatesMixin from '@/components/mixins/Templates.vue'

const TYPES = ['post', 'text', 'media', 'affix']

export default {
    name: 'TemplatesManagement',

    components: { TemplatePicker, TemplatePreview, MediaTemplateUploader, Picker },

    mixins: [TemplatesMixin],

    data() {
        return {
            TYPES,
            activeType: 'post',
            selected: null,
            editText: '',
            editPrefix: '',
            editSuffix: '',
            editFiles: [],
            saving: false,

            creating: false,
            creatingInProgress: false,
            createName: '',
            createText: '',
            createPrefix: '',
            createSuffix: '',
            createFiles: [],
            // bumped on every startCreate() so <MediaTemplateUploader :key> remounts fresh -
            // otherwise cancelling a media creation and starting another would keep showing the
            // previous attempt's files (its own item list is internal, v-model resetting
            // createFiles to [] from here doesn't reach back into it).
            createUploaderKey: 0,

            // 'editText' | 'editPrefix' | 'editSuffix' | 'createText' | 'createPrefix' |
            // 'createSuffix' | null - doubles as both the data property name AND the matching
            // ref name for whichever field's emoji flyout is open (see insertEmoji).
            openEmojiPickerFor: null,
            emojiIndex: new EmojiIndex(emojiData, {
                recentLength: 18,
                recent: ['tada', 'sparkles', 'rocket', 'confetti_ball', 'fire', 'heart', 'speech_balloon', 'heart_eyes', 'raised_hands', 'two_hearts', 'bulb', 'brain', 'loudspeaker', 'star2', 'wrench', 'hammer_and_wrench', 'calendar', 'spiral_calendar_pad']
            })
        }
    },

    computed: {
        canCreate() {
            if (this.creatingInProgress || !this.createName.trim()) return false
            if (this.activeType === 'text') return !!this.createText.trim()
            if (this.activeType === 'affix') return !!(this.createPrefix.trim() || this.createSuffix.trim())
            if (this.activeType === 'media') return this.createFiles.length > 0
            return false
        },

        // media saved from the media editor carries overlay/timeline personalization on top of
        // its files - not reconstructible through a plain file-list UI, so those stay read-only
        // (matches the original design). Media saved from the post creator's Médias field (or
        // created here) is just files - fully editable.
        selectedHasOverlay() {
            return !!(this.selected && this.selected.data && this.selected.data.overlay)
        }
    },

    methods: {
        switchType(t) {
            this.activeType = t
            this.selected = null
            this.creating = false
        },

        onSelect(template) {
            this.creating = false
            this.selected = template
            this.editText = (template.data && template.data.text) || ''
            this.editPrefix = (template.data && template.data.prefix) || ''
            this.editSuffix = (template.data && template.data.suffix) || ''
            // editFiles itself is populated by MediaTemplateUploader's own mounted() hook (seeded
            // from initial-files, keyed by template.id) - just clear the previous selection's
            // value here so canCreate/the save button don't briefly reflect stale data.
            this.editFiles = []

            if (template.type === 'text') {
                this.$nextTick(() => this.sizeTextarea('editText'))
            } else if (template.type === 'affix') {
                this.$nextTick(() => this.sizeTextarea('editPrefix'))
            }
        },

        onDeleted(id) {
            if (this.selected && this.selected.id === id) this.selected = null
        },

        startCreate() {
            this.selected = null
            this.creating = true
            this.createName = ''
            this.createText = ''
            this.createPrefix = ''
            this.createSuffix = ''
            this.createFiles = []
            this.createUploaderKey += 1

            if (this.activeType === 'text') {
                this.$nextTick(() => this.sizeTextarea('createText'))
            } else if (this.activeType === 'affix') {
                this.$nextTick(() => this.sizeTextarea('createPrefix'))
            }
        },

        cancelCreate() {
            this.creating = false
        },

        async submitCreate() {
            if (!this.canCreate) return

            const data = this.activeType === 'text' ? { text: this.createText }
                : this.activeType === 'affix' ? { prefix: this.createPrefix, suffix: this.createSuffix }
                : { files: this.createFiles }

            this.creatingInProgress = true
            const res = await this.newTemplate({
                type: this.activeType,
                name: this.createName.trim(),
                data,
                folder_id: null,
                position: 0
            })
            this.creatingInProgress = false

            if (res && res.body && res.body.success) {
                await this.$store.dispatch('templates/fetchTemplates', this.activeType)
                this.creating = false
            }
        },

        toggleEmojiPicker(field) {
            this.openEmojiPickerFor = this.openEmojiPickerFor === field ? null : field
        },

        // affix's prefix+suffix share one column and must be resized together: shrinking one
        // wrapper's flex share is what lets the other reclaim that freed space instead of leaving
        // a gap, so both need remeasuring from a common flex:1 1 0 baseline in the same pass (see
        // sizeTextareaGroup). A plain text field is just a "group" of one.
        textareaGroupFor(refName) {
            if (refName.includes('Prefix') || refName.includes('Suffix')) {
                const base = refName.startsWith('create') ? 'create' : 'edit'
                return [`${base}Prefix`, `${base}Suffix`]
            }
            return [refName]
        },

        sizeTextarea(refName) {
            this.sizeTextareaGroup(this.textareaGroupFor(refName))
        },

        // .tm-emoji-field (each textarea's direct parent) is flex:1 1 0 within .tm-detail's
        // column by default - a share that's independent of its own textarea's content, only of
        // its siblings' natural sizes and however many .tm-emoji-fields are competing for space.
        // Reset every field in the group back to that default first so each one's ceiling is
        // measured fresh, not against whatever a previous call already shrank it to (that would
        // make the ceiling drift down every time this runs). Every field's wrapper is then PINNED
        // to its own min(content, ceiling) height, whether that's below or at the ceiling - never
        // left at flex:1 1 0 once measured. Leaving a capped field at flex:1 was the earlier bug:
        // once a sibling field shrank to flex:0 0 auto, this field - now the only flex:1 1 0 item
        // left - would silently re-claim ALL the space the sibling just freed, growing its wrapper
        // (blank, since its textarea itself stays capped) far past its own ceiling and stranding
        // whatever comes after it (the next label, or the save button) way down the column.
        sizeTextareaGroup(refNames) {
            const entries = refNames
                .map(refName => {
                    const el = this.$refs[refName]
                    const field = el && el.closest('.tm-emoji-field')
                    return field ? { el, field } : null
                })
                .filter(Boolean)
            if (!entries.length) return

            entries.forEach(({ field }) => {
                field.style.flex = ''
                field.style.height = ''
            })

            entries.forEach(({ el, field }) => {
                const ceiling = field.clientHeight

                el.style.height = 'auto'
                const desired = el.scrollHeight
                const height = Math.min(desired, ceiling)
                el.style.height = `${height}px`
                el.style.overflowY = desired > ceiling ? 'auto' : 'hidden'

                field.style.flex = '0 0 auto'
                field.style.height = `${height}px`
            })
        },

        // field is both the data property holding the text AND the matching ref name (see
        // data()'s comment) - genuinely generic across all 6 text/affix fields, create and edit.
        insertEmoji(field, emoji) {
            const el = this.$refs[field]
            const current = this[field] || ''
            const start = el ? el.selectionStart : current.length
            const end = el ? el.selectionEnd : current.length
            this[field] = current.slice(0, start) + emoji.native + current.slice(end)
            this.openEmojiPickerFor = null

            this.$nextTick(() => {
                if (!el) return
                const pos = start + emoji.native.length
                el.focus()
                el.setSelectionRange(pos, pos)
                this.sizeTextarea(field)
            })
        },

        async saveText() {
            this.saving = true
            const res = await this.updateTemplate({ id: this.selected.id, data: { text: this.editText } })
            this.saving = false

            if (res && res.body && res.body.success) {
                this.selected = res.body.template
                await this.$store.dispatch('templates/fetchTemplates', this.activeType)
            }
        },

        async saveAffix() {
            this.saving = true
            const res = await this.updateTemplate({ id: this.selected.id, data: { prefix: this.editPrefix, suffix: this.editSuffix } })
            this.saving = false

            if (res && res.body && res.body.success) {
                this.selected = res.body.template
                await this.$store.dispatch('templates/fetchTemplates', this.activeType)
            }
        },

        async saveMedia() {
            if (!this.editFiles.length) return
            this.saving = true
            const res = await this.updateTemplate({ id: this.selected.id, data: { files: this.editFiles } })
            this.saving = false

            if (res && res.body && res.body.success) {
                this.selected = res.body.template
                await this.$store.dispatch('templates/fetchTemplates', this.activeType)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.templates-management {
    padding: 30px 40px;
    height: 100%;
    display: flex;
    flex-direction: column;

    .tm-header {
        margin-bottom: 16px;

        .tm-title {
            font-size: 22px;
            font-weight: 700;
            color: #121826;
        }
    }

    .tm-tabs {
        display: flex;
        gap: 6px;
        border-bottom: 1px solid #e3e6ee;
        margin-bottom: 20px;

        .tm-tab {
            padding: 10px 16px;
            font-size: 14px;
            font-weight: 600;
            color: #9ba3b7;
            cursor: pointer;
            border-bottom: 2px solid transparent;

            &.active {
                color: #6b1f4d;
                border-color: #6b1f4d;
            }
        }
    }

    .tm-body {
        flex: 1;
        display: flex;
        gap: 24px;
        min-height: 0;
    }

    // Rebalanced from the original 360px-tree/flex:1-detail split: browsing/organizing (the
    // tree - search, folders, drag-reorder, rename/move/delete) is where almost all the real
    // action happens, while the detail panel is just one small field + a save button (or a
    // read-only note for post/media) - it doesn't need to fill whatever's left of a wide screen.
    // both panels share the same fixed viewport-relative height (roughly this page's own
    // header/tabs height) so they stay visually matched; .tm-tree scrolls its list normally,
    // .tm-detail never scrolls itself (see its own rule) - only its textareas do.
    .tm-tree {
        flex: 1 1 420px;
        min-width: 320px;
        height: calc(100vh - 390px);
        overflow-y: auto;
        border: 1px solid #e3e6ee;
        border-radius: 8px;
        padding: 12px;
    }

    .tm-detail {
        flex: 0 1 420px;
        max-width: 420px;
        // a real, content-independent height (not max-height) - needed so the flex children
        // below (.tm-create-form / .tm-emoji-field) have actual space to distribute via flex:1,
        // rather than the panel's own height being driven by their content (circular). Nothing
        // in this column ever needs to scroll except the textareas themselves (overflow:hidden) -
        // .tm-emoji-field's flex:1 share is what caps each textarea's available height, and one
        // or two of them (affix) share it via equal flex:1 splits, naturally leaving each a bit
        // under 50% once the labels/name/button also claim their own natural share.
        height: calc(100vh - 390px);
        border: 1px solid #e3e6ee;
        border-radius: 8px;
        padding: 20px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .tm-detail-header,
        .tm-detail-name,
        .tm-detail-source,
        .tm-field-label,
        .tm-btn-save,
        .tm-preview-large,
        .tm-readonly-hint,
        .tm-empty {
            flex: 0 0 auto;
        }

        .tm-detail-header {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
        }

        .tm-create-form {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
        }

        .tm-btn-new {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            font-weight: 600;
            color: #6b1f4d;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }

        .tm-empty {
            color: #9ba3b7;
            font-size: 14px;
            text-align: center;
            margin-top: 40px;
        }

        .tm-detail-name {
            font-size: 16px;
            font-weight: 700;
            color: #121826;
            margin-bottom: 4px;
        }

        .tm-detail-source {
            font-size: 12px;
            color: #9ba3b7;
            margin-bottom: 16px;
        }

        .tm-field-label {
            display: block;
            font-size: 13px;
            font-weight: 600;
            color: #121826;
            margin: 12px 0 6px;
        }

        .tm-textarea,
        .tm-input {
            width: 100%;
            border: 1px solid #e3e6ee;
            border-radius: 6px;
            padding: 10px;
            font-size: 13px;
            outline: none;
            font-family: inherit;
            // explicit light colors: without them, a browser in dark OS mode renders these
            // unstyled native controls with white-on-white text (invisible) - color-scheme:light
            // pins the native control rendering itself regardless of OS/browser preference.
            color-scheme: light;
            background-color: #fff;
            color: #121826;
        }

        // .tm-emoji-field (flex:1 below) sets the MAX available height - a stable ceiling that
        // doesn't depend on this textarea's own content (see sizeTextarea's comment). sizeTextarea
        // pins an explicit px height that shrinks to fit short content and stops at that ceiling
        // for long content, where the native scrollbar takes over (overflow-y:auto). flex:0 0 auto
        // (not 1 1 auto/growing) is deliberate: sizeTextarea's `height:'auto'` reset relies on the
        // element actually collapsing to its intrinsic size to read a correct scrollHeight - a
        // growing flex item would just re-stretch itself and scrollHeight would report that
        // stretched size back, defeating the shrink-to-content measurement entirely.
        textarea.tm-textarea,
        .tm-affix-textarea {
            resize: none;
            overflow-y: auto;
            flex: 0 0 auto;
            min-height: 0;
            // reserves the scrollbar's track even when not actively scrolling, so the emoji
            // trigger (offset to sit just left of it, see .tm-emoji-trigger) never sits under the
            // scrollbar thumb once one appears. Falls back to no reservation on older browsers,
            // same as before this rule existed.
            scrollbar-gutter: stable;
        }

        .tm-affix-textarea {
            line-height: 1.4;
        }

        // wraps a textarea + its emoji trigger/flyout - same idea as TemplatePicker.vue's
        // .input-textarea, just in this page's own light theme instead of the popup's dark one.
        // flex:1 1 0 so, when there are two (affix), they split .tm-detail's remaining height
        // equally between them; flex column + min-height:0 so the textarea's own flex:1 above
        // actually has a bounded box to fill instead of growing to content.
        .tm-emoji-field {
            position: relative;
            display: flex;
            flex-direction: column;
            flex: 1 1 0;
            min-height: 0;

            .tm-textarea,
            .tm-affix-textarea {
                // wide enough that typed text never runs under the trigger icon, and clear of the
                // reserved scrollbar-gutter zone too (see scrollbar-gutter:stable above)
                padding-right: 40px;
            }

            .tm-emoji-trigger {
                position: absolute;
                top: 8px;
                // past the reserved scrollbar-gutter zone (native scrollbars run ~15-17px on
                // Windows/Linux Chrome/Firefox) - otherwise the scrollbar thumb, once a long
                // text triggers one, renders directly on top of this icon.
                right: 20px;
                height: 20px;
                width: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                color: #9ba3b7;
                font-size: 14px;

                &:hover {
                    color: #6b1f4d;
                }
            }

            .tm-emoji-picker {
                position: absolute;
                top: calc(100% + 6px);
                right: 0;
                z-index: 20;
                width: 300px;
            }
        }

        .tm-form-actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
            margin-top: 16px;

            .tm-btn-save {
                margin-top: 0;
            }
        }

        .tm-btn-cancel {
            display: flex;
            align-items: center;
            padding: 8px 16px;
            font-size: 13px;
            font-weight: 600;
            color: #6b7280;
            cursor: pointer;
            border-radius: 6px;

            &:hover {
                background: #f5f6fa;
            }
        }

        .tm-btn-save {
            margin-top: 16px;
            border: none;
            border-radius: 6px;
            padding: 8px 16px;
            font-size: 13px;
            font-weight: 600;
            background: #6b1f4d;
            color: #fff;
            cursor: pointer;

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .tm-preview-large {
            width: 160px;
            height: 160px;
            margin-bottom: 16px;
        }

        .tm-readonly-hint {
            font-size: 13px;
            color: #9ba3b7;
        }
    }
}
</style>
